import { HttpErrorResponse } from '@angular/common/http'
import { Injectable, NgZone } from '@angular/core'
import { ActivatedRoute, Params, Router } from '@angular/router'
import { Store } from '@ngrx/store'
import { addSeconds, Duration, formatDuration } from 'date-fns'
import { BehaviorSubject, EMPTY, noop, Observable, of, throwError } from 'rxjs'
import { fromPromise } from 'rxjs/internal/observable/innerFrom'
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators'
import { appConfig } from '../../../brands/_current/config/app-config'
import { setTemporaryBlockExpiration } from '../../account/store/actions'
import { AnalyticsEventType, AnalyticsService } from '../../shared/analytics/analytics.service'
import { getCountdownDuration } from '../../shared/countdown-duration'
import { LocalizationDateFnsService } from '../../shared/localisation-date-fns.service'
import { MyCiceroWebApiService } from '../../shared/mycicero-web-api/my-cicero-web-api.service'
import { AcceptAuthorizeConsentsInputmodel } from '../../shared/mycicero-web-api/shared/accept-authorize-consents-input.model'
import { CodeError } from '../../shared/mycicero-web-api/shared/base-output.model'
import { ChangeRegistrationCellAndSendSmsOutput } from '../../shared/mycicero-web-api/shared/change-registration-cell-and-send-sms-output.model'
import { ConfirmEmailOTPOutput } from '../../shared/mycicero-web-api/shared/confirm-emai-otp-output.model'
import { GetAuthorizeConsentsOutput } from '../../shared/mycicero-web-api/shared/get-authorize-consents-output.model'
import { OtpForAccountMatchingType } from '../../shared/mycicero-web-api/shared/otp-for-account-matching-type.enum'
import { ResendSmsOutput } from '../../shared/mycicero-web-api/shared/resend-sms-output.model'
import { SendOtpForAccountMatchingOutput } from '../../shared/mycicero-web-api/shared/send-otp-for-account-matching-output.model'
import { ToastDuration } from '../../shared/toast-extension/toast-duration.enum'
import { ToastSeverity } from '../../shared/toast-extension/toast-severity.enum'
import { MessageService } from '../../shared/toast-module/messageservice'
import { PendingOperationEnum } from '../pending-operation.enum'
import { OperationType, PendingData, PendingPageData } from './pending-data.model'
import { temporaryBlockedRetryUrl } from './pending-operation-params'

/**
 * Service for get and update the information about the user pending data (e.g.: mobile phone, acceptance of terms  , ...)
 * It is provided in root because some data needs to be shared by the whole application (e.g: {@see smsDispatchExpiryDateSubject$})
 */
@Injectable({
  providedIn: 'root',
})
export class PendingOperationService {
  private readonly smsDispatchExpiryDateSubject$: BehaviorSubject<Date> = new BehaviorSubject<Date>(null)
  private readonly smsDispatchExpireDate$: Observable<Date> = this.smsDispatchExpiryDateSubject$.asObservable()
  private readonly emailDispatchExpiryDateSubject$: BehaviorSubject<Date> = new BehaviorSubject<Date>(null)
  private readonly emailDispatchExpiryDate$: Observable<Date> = this.emailDispatchExpiryDateSubject$.asObservable()
  private pendingData: PendingData | null = null

  constructor(
    private readonly myCiceroWebApi: MyCiceroWebApiService,
    private readonly messageService: MessageService,
    private readonly router: Router,
    private readonly zone: NgZone,
    private readonly analyticsService: AnalyticsService,
    private readonly localizationDateFnsService: LocalizationDateFnsService,
    private readonly store: Store,
  ) {}

  /**
   * Sends a request to resend the SMS for phone confirmation.
   * Displays an error message if the request fails.
   * @param recaptchaValue The recaptcha token for validation.
   * @returns An observable that emits true if the SMS was resent or action is required, false otherwise.
   */
  resendSms(recaptchaValue: string): Observable<boolean> {
    return this.myCiceroWebApi.resendSms(recaptchaValue).pipe(
      tap((output: ResendSmsOutput) => {
        if (output.DateTimeNextRequestIsAvailable) {
          this.setFormattedSmsDispatchExpiryDate(output.DateTimeNextRequestIsAvailable)
        }
      }),
      map(() => true),
      catchError((err) =>
        this.zone.run(() => {
          if (err instanceof HttpErrorResponse) {
            if ((err.error.ActionRequired as PendingOperationEnum) === PendingOperationEnum.needConfirmPhone) {
              return of(true)
            }
            /**
             * If OTP_BLOCKED occurs, the smsDispatchExpiryDateSubject$ is updated
             * and the error is not displayed via the message service
             */
            if (
              Object.values(CodeError).includes(err.error.CodeError) &&
              (err.error.CodeError as CodeError) === CodeError.otpBlocked &&
              err.error.DateTimeNextRequestIsAvailable
            ) {
              this.setFormattedSmsDispatchExpiryDate(err.error.DateTimeNextRequestIsAvailable)
            } else {
              /**
               * In all other error cases, the error is displayed via messageService
               * and the smsDispatchExpiryDateSubject$ is not updated, and
               */
              this.messageService.add({
                severity: ToastSeverity.Error,
                life: ToastDuration.Long,
                id: 'signUp.sendSms.retryMessage',
                summary: 'Uh oh',
                detail: 'Si è verificato un errore',
              })
            }
          }
          console.error(err)
          return of(false)
        }),
      ),
    )
  }

  /**
   * Sends a request to resend the OTP for phone confirmation.
   * Updates the SMS dispatch expiry date if needed and displays an error message on failure.
   * @param recaptchaValue The recaptcha token for validation.
   * @returns An observable that emits true if the OTP was resent, false otherwise.
   */
  resendPhoneOTP(recaptchaValue: string): Observable<boolean> {
    return this.myCiceroWebApi.resendPhoneOTP(recaptchaValue).pipe(
      map((data) => {
        /**
         * If the action_required is not handled via the interceptor ({@see PendingOperationInterceptor}) then we add the timeout here
         */
        this.setFormattedSmsDispatchExpiryDate(data.OTPRequestBlockedUntil)
        return true
      }),
      catchError((error) =>
        this.zone.run(() => {
          console.error(error)
          this.messageService.add({
            severity: ToastSeverity.Error,
            life: ToastDuration.Long,
            id: 'signUp.sendSms.retryMessage',
            summary: 'Uh oh',
            detail: 'Si è verificato un errore',
          })
          return of(false)
        }),
      ),
    )
  }

  /**
   * Confirms the mobile phone using the provided confirmation code and recaptcha token.
   * Tracks analytics events and redirects the user on success.
   * @param confirmationCode The code received via SMS.
   * @param recaptchaValue The recaptcha token for validation.
   * @returns An observable that emits true if confirmation is successful, or navigates to the success page.
   */
  confirmMobile(confirmationCode: string, recaptchaValue: string): Observable<boolean> {
    return this.myCiceroWebApi.confirmPinSms(confirmationCode, recaptchaValue).pipe(
      switchMap((response) => {
        if (!response.Success) {
          throwError(() => new Error(response.MessageError))
        }

        if (appConfig.analytics.events.signUpSuccess?.googleGlobalSiteTag?.action) {
          this.analyticsService.eventTrack(
            AnalyticsEventType.googleGlobalSiteTag,
            appConfig.analytics.events.signUpSuccess.googleGlobalSiteTag.action,
            {
              gstCustom: {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                send_to: appConfig.analytics.events.signUpSuccess.googleGlobalSiteTag.sendTo,
              },
            },
          )
        }

        if (appConfig.analytics.events.signUpSuccess?.metaPixel?.action) {
          this.analyticsService.eventTrack(
            AnalyticsEventType.facebook,
            appConfig.analytics.events.signUpSuccess.metaPixel.action,
          )
        }

        /**
         * If it is a confirmation without auto-login,
         * the confirmPinSms method will send a success response with an empty UrlRedirect value.
         * So, in this case, the user will be directly redirect to the success page.
         * Otherwise, the {@see AccountStatusInterceptor} will redirect to the proper page.
         */
        if (!response.UrlRedirect) {
          return fromPromise(
            this.router.navigate(['/', appConfig.signUp.paths._root, appConfig.feedback.paths.success], {
              queryParamsHandling: 'merge',
            }),
          )
        }

        return of(true)
      }),
      catchError((error) => {
        console.error(error)
        return throwError(() => error)
      }),
    )
  }

  /**
   * Updates the mobile number and sends an SMS for confirmation.
   * Updates the SMS dispatch expiry date if needed.
   * @param mobileCountryCode The country code of the mobile number.
   * @param mobileNumber The mobile number to update.
   * @param recaptchaToken The recaptcha token for validation.
   * @returns An observable with the output of the change and SMS send operation.
   */
  updateMobileAndSendSms(
    mobileCountryCode: string,
    mobileNumber: string,
    recaptchaToken: string,
  ): Observable<ChangeRegistrationCellAndSendSmsOutput> {
    return this.myCiceroWebApi.changeRegistrationCellAndSendSms(mobileCountryCode, mobileNumber, recaptchaToken).pipe(
      tap((response) => {
        if (response.Success && response.DateTimeNextRequestIsAvailable) {
          this.setFormattedSmsDispatchExpiryDate(response.DateTimeNextRequestIsAvailable)
        }
      }),
      catchError((error) => {
        console.error(error)
        return throwError(() => error)
      }),
    )
  }

  /**
   * Sets the expiry date for SMS dispatch by adding the specified seconds to the current time.
   * @param seconds The number of seconds until expiry.
   * @returns The calculated expiry date.
   */
  setSmsDispatchExpiryDate(seconds: number): Date {
    const date = addSeconds(new Date(), seconds)
    this.smsDispatchExpiryDateSubject$.next(date)
    return date
  }

  /**
   * Sets the expiry date for SMS dispatch using a formatted date string.
   * @param date The formatted date string.
   * @returns The parsed expiry date.
   */
  setFormattedSmsDispatchExpiryDate(date: string): Date {
    const formattedDate = new Date(date)
    this.smsDispatchExpiryDateSubject$.next(formattedDate)
    return formattedDate
  }

  /**
   * Sets the expiry date for email dispatch using a formatted date string.
   * @param date The formatted date string.
   * @returns The parsed expiry date.
   */
  setFormattedEmailDispatchExpiryDate(date: string): Date {
    const formattedDate = new Date(date)
    this.emailDispatchExpiryDateSubject$.next(formattedDate)
    return formattedDate
  }

  /**
   * Changes the user's password using the provided new password and recaptcha token.
   * @param newPassword The new password to set.
   * @param recaptchaToken The recaptcha token for validation.
   * @returns An observable that emits true if the password was changed successfully.
   */
  changePassword(newPassword: string, recaptchaToken: string): Observable<boolean> {
    return this.myCiceroWebApi.changePassword(newPassword, recaptchaToken).pipe(
      map(() => true),
      catchError((error) => throwError(() => error)),
    )
  }

  /**
   * Resends the OTP for email confirmation.
   * @param recaptchaValue The recaptcha token for validation.
   * @returns An observable that emits true if the OTP was resent, false otherwise.
   */
  resendEmailOTP(recaptchaValue: string): Observable<boolean> {
    return this.myCiceroWebApi.resendEmailOTP(recaptchaValue).pipe(
      map((data) => {
        return data.Success
      }),
      catchError(() => of(false)),
    )
  }

  /**
   * Confirms the email using the provided OTP code and recaptcha token.
   * @param otpCode The OTP code received via email.
   * @param recaptchaValue The recaptcha token for validation.
   * @returns An observable with the output of the confirmation operation.
   */
  confirmEmailOTP(otpCode: string, recaptchaValue: string): Observable<ConfirmEmailOTPOutput> {
    return this.myCiceroWebApi
      .confirmEmailOTP(otpCode, recaptchaValue)
      .pipe(catchError((error) => throwError(() => error)))
  }

  /**
   * Confirms the user's email address using the recaptcha token.
   * @param recaptchaValue The recaptcha token for validation.
   * @returns An observable that emits true if the email was confirmed successfully.
   */
  confirmEmail(recaptchaValue: string): Observable<boolean> {
    return this.myCiceroWebApi.confirmEmail(recaptchaValue).pipe(
      map((result) => result.Success),
      catchError((error) => throwError(() => error)),
    )
  }

  /**
   *
   * @param recaptchaValue recaptcha
   */
  startEmailCertification(recaptchaValue: string) {
    return this.myCiceroWebApi
      .startEmailCertification(recaptchaValue)
      .pipe(map((response) => response.DateTimeNextRequestsAvailable))
  }

  /**
   *
   * @param recaptchaValue recaptcha
   */
  checkEmailCertification(recaptchaValue: string) {
    return this.myCiceroWebApi
      .checkEmailCertification(recaptchaValue)
      .pipe(catchError((error) => throwError(() => error)))
  }

  /**
   *
   * @param token token
   * @param userId userid
   * @param recaptchaResponse recaptcha response
   */
  confirmEmailCertification(token: string, userId: string, recaptchaResponse: string) {
    return this.myCiceroWebApi.confirmEmailCertification(token, userId, recaptchaResponse)
  }

  /**
   * Sets the user's mobile number using the provided country code, number, and recaptcha token.
   * @param mobileCountryCode The country code of the mobile number.
   * @param mobileNumber The mobile number to set.
   * @param recaptchaToken The recaptcha token for validation.
   * @returns An observable that emits true if the mobile number was set successfully.
   */
  setMobileNumber(mobileCountryCode: string, mobileNumber: string, recaptchaToken: string): Observable<boolean> {
    return this.myCiceroWebApi
      .insertPhone(mobileCountryCode, mobileNumber, recaptchaToken)
      .pipe(map((response) => response.Success))
  }

  /**
   * Gets the countdown duration for SMS dispatch expiry.
   * @returns An observable with the countdown duration or null.
   */
  getSmsCountDown(): Observable<Duration | null> {
    return this.smsDispatchExpireDate$.pipe(switchMap((date) => getCountdownDuration(date)))
  }

  /**
   * Gets the countdown duration for email dispatch expiry.
   * @returns An observable with the countdown duration or null.
   */
  getEmailCountDown(): Observable<Duration | null> {
    return this.emailDispatchExpiryDate$.pipe(switchMap((d) => getCountdownDuration(d)))
  }

  /**
   * Sends an OTP for account matching (email or SMS) and updates the expiry date if needed.
   * Displays an error message on failure.
   * @param type The type of OTP to send (email or SMS).
   * @param recaptchaToken The recaptcha token for validation.
   * @returns An observable that emits true if the OTP was sent, false otherwise.
   */
  sendOtpForAccountMatching(type: OtpForAccountMatchingType, recaptchaToken: string): Observable<boolean> {
    return this.myCiceroWebApi.sendOtpForAccountMatching(type, recaptchaToken).pipe(
      tap((output: SendOtpForAccountMatchingOutput) => {
        if (output.DateTimeNextRequestIsAvailable) {
          if (type === OtpForAccountMatchingType.email) {
            this.setFormattedEmailDispatchExpiryDate(output.DateTimeNextRequestIsAvailable)
          } else if (type === OtpForAccountMatchingType.sms) {
            this.setFormattedSmsDispatchExpiryDate(output.DateTimeNextRequestIsAvailable)
          }
        }
      }),
      map(() => true),
      catchError((error) => {
        console.error(error)
        this.messageService.add({
          severity: ToastSeverity.Error,
          life: ToastDuration.Long,
          id: `externalMatching.${type.toLowerCase()}.retryMessage`,
          summary: 'Uh oh',
          detail: 'Si è verificato un errore',
        })
        return of(false)
      }),
    )
  }

  /**
   * Confirms the OTP for account matching and redirects the user on success.
   * @param type The type of OTP to confirm (email or SMS).
   * @param otpCode The OTP code received.
   * @param recaptchaToken The recaptcha token for validation.
   * @returns An observable that emits true if confirmation is successful, or navigates to the success page.
   */
  confirmOtpForAccountMatching(
    type: OtpForAccountMatchingType,
    otpCode: string,
    recaptchaToken: string,
  ): Observable<boolean> {
    return this.myCiceroWebApi.confirmOtpForAccountMatching(type, otpCode, recaptchaToken).pipe(
      switchMap((response) => {
        /**
         * If it is a confirmation without auto-login
         * the confirmPinSms method will send a success response with an empty UrlRedirect value.
         * So, in this case, the user will be directly redirect to the success page.
         * Otherwise, the {@see AccountStatusInterceptor} will redirect to the proper page.
         */
        if (!response.UrlRedirect) {
          return fromPromise(
            this.router.navigate(['/', appConfig.signUp.paths._root, appConfig.feedback.paths.success], {
              queryParamsHandling: 'merge',
            }),
          )
        }
        return of(true)
      }),
      catchError((error) => throwError(() => error)),
    )
  }

  /**
   * Sets the pending data for the current operation.
   * @param pendingData The pending data to set, or null to clear.
   */
  setPendingData(pendingData: PendingData | null) {
    this.pendingData = pendingData
  }

  /**
   * Gets the current pending data for the operation.
   * @returns The pending data object or null if not set.
   */
  getPendingData(): PendingData | null {
    return this.pendingData
  }

  /**
   * Formats a Duration object into a human-readable string.
   *
   * The output includes hours, minutes and seconds, omitting zero values
   * and using the current locale provided by the localization service.
   * @param d - The duration to format.
   * @returns A localized, human-readable string representation of the duration.
   */
  formatDurationToString(d: Duration): string {
    const text = formatDuration(d, {
      format: ['hours', 'minutes', 'seconds'],
      delimiter: ', ',
      locale: this.localizationDateFnsService.getCurrentLocale(),
      zero: false,
    })
    return text.trim()
  }

  /**
   * Obtains consents list for pending operation "information-sharing"
   */
  getAuthorizeConsents(): Observable<GetAuthorizeConsentsOutput> {
    return this.myCiceroWebApi.getAuthorizeConsents().pipe(
      catchError(() => {
        this.messageService.add({
          severity: ToastSeverity.Error,
          life: ToastDuration.Long,
          summary: 'Uh oh',
          detail: $localize`Si è verificato un errore. Riprova più tardi`,
        })
        return EMPTY
      }),
    )
  }

  /**
   * sends accepted consents list
   * @param data consents list
   */
  acceptAuthorizeConsents(data: AcceptAuthorizeConsentsInputmodel) {
    return this.myCiceroWebApi.acceptAuthorizeConsents(data).pipe(
      catchError(() => {
        this.messageService.add({
          severity: ToastSeverity.Error,
          life: ToastDuration.Long,
          summary: 'Uh oh',
          detail: $localize`Si è verificato un errore. Riprova più tardi`,
        })
        return EMPTY
      }),
    )
  }

  /**
   * gets pending operation information from route; used in email-verification and external-email-verification
   * @param route current route
   */
  getPendingDataFromRoute(route: ActivatedRoute) {
    return route.data.pipe(
      map((data) => data as PendingPageData),
      map((data) => ({
        ...data,
        description:
          data.operationType === OperationType.externalMatching && data.pendingData?.email
            ? `${data.description} <strong>${data.pendingData?.email}</strong>`
            : data.description,
      })),
      shareReplay(1),
    )
  }

  /**
   * redirect to temporary blocked page
   */
  redirectToTemporaryBlocked(router: Router, date: Date, queryParams?: Params) {
    this.store.dispatch(setTemporaryBlockExpiration({ date }))
    const currentUrl = router.url.split('?')[0].split('/').filter(Boolean)
    router
      .navigate(['/', appConfig.account.paths._root, appConfig.account.paths.temporaryBlocked], {
        queryParams: queryParams ?? null,
        queryParamsHandling: 'merge',
        state: {
          [temporaryBlockedRetryUrl]: ['/', ...currentUrl],
        },
      })
      .then(noop)
  }
}
